/**
 * Module dependencies.
 */

import { get, range } from 'lodash';
import React, { ComponentType, useState, useMemo, ReactNode, useEffect, Fragment } from 'react';
import { cn } from 'app/lib/utils';
import { Header } from './header';
import { Expanded } from './expanded';
import { useSearchParams } from 'react-router-dom';
import { castFunction } from 'app/core/utils/template';
import { Spinner } from 'app/components/ui/spinner';
import { Pagination, TablePagination } from './table-pagination';

/**
 * `Column` type.
 */

export type Column<T> = {
  title: string;
  size?: string;
  dataIndex?: string;
  key: string;
  mobile?: {
    direction?: 'column' | 'row';
  };
  style?: any;
  styleRow?: any;
  render?: (item: T) => ReactNode;
};

/**
 * `Props` type.
 */

type Props<T> = {
  isLoading?: boolean;
  className?: string;
  pagination?: Pagination;
  columns: Column<T>[];
  dataSource: T[];
  changePageSize?: (value: number) => void;
  onClickLine?: (item: T) => void;
  footer?: JSX.Element;
  rowKey: string;
  sortable?: boolean;
  hasClickableRow?: (item: T) => boolean;
  expandedRowRender?: ComponentType<any>;
  expandedExtras?: any;
  style?: any;
  wrapperClassName?: string;
  rowClassName?: (item: T, index: number) => string | undefined;
};

/**
 * Export `Table` component.
 */

export function Table<T>(props: Props<T>): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeRow, setActiveRow] = useState<number>(
    searchParams.get('activeRow') ? parseInt(searchParams.get('activeRow') ?? '-1') : -1
  );

  const {
    isLoading,
    className,
    footer,
    pagination,
    changePageSize,
    columns,
    dataSource,
    onClickLine,
    hasClickableRow,
    expandedExtras,
    expandedRowRender,
    rowKey,
    wrapperClassName,
    rowClassName
  } = props;

  const style = useMemo(() => {
    const gridTemplateColumns = columns.map(item => {
      return item.size ?? 'max-content';
    });

    const gridTemplateAreas = columns.map(item => {
      return item.key;
    });

    const expandCode = !expandedRowRender
      ? ''
      : range(columns.length)
          .map(() => 'expand')
          .join(' ');

    return {
      header: {
        gridTemplateColumns: gridTemplateColumns.join(' ')
      },
      row: {
        gridTemplateColumns: gridTemplateColumns.join(' '),
        gridTemplateAreas: `
          "${gridTemplateAreas.join(' ')}"
          ${!!expandCode ? `"${expandCode}"` : ''}
        `
      }
    };
  }, [columns, expandedRowRender]);

  useEffect(() => {
    setSearchParams(searchParams => {
      if (activeRow === -1) {
        searchParams.delete('activeRow');
      } else {
        searchParams.set('activeRow', String(activeRow));
      }

      return searchParams;
    });
  }, [activeRow, setSearchParams]);

  return (
    <div className={'relative overflow-x-auto overflow-y-visible -mx-4 px-4'}>
      <div className={cn('min-h-[250px]', wrapperClassName)}>
        <div
          className={cn(
            'grid w-full transition-[filter] duration-[350ms] ease-in-out max-[600px]:flex max-[600px]:flex-col max-[600px]:mt-5',
            className,
            isLoading && 'blur-[4px]'
          )}
          style={{
            ...style.header,
            ...(props.style ?? {})
          }}
        >
          <Header columns={columns} />

          {(dataSource ?? []).map((item, index) => {
            const ExpandedRow = expandedRowRender;
            const hasClickable = hasClickableRow ? !!onClickLine || hasClickableRow(item) : true;

            return (
              <Fragment key={`${get(item, rowKey)}-${index}`}>
                <div
                  className={cn(
                    'contents max-[600px]:flex max-[600px]:flex-col max-[600px]:rounded-lg max-[600px]:overflow-hidden max-[600px]:shadow-[0px_0px_5px_0px_rgba(0,0,0,0.30)] max-[600px]:mb-4 max-[600px]:p-4 max-[600px]:bg-[#f2f2f2]',
                    (!!expandedRowRender || !!hasClickable) && 'cursor-pointer [&:hover_.cell]:bg-[#f7f7f8]',
                    index === activeRow && !!expandedRowRender && hasClickable && '[&_.cell]:bg-[#f4f4f5]',
                    rowClassName?.(item, index)
                  )}
                  onClick={() => {
                    if (onClickLine) {
                      return onClickLine(item);
                    }

                    if (!ExpandedRow || !hasClickable) {
                      return;
                    }

                    setActiveRow(activeRow === index ? -1 : index);
                  }}
                >
                  {columns.map(({ key, title, dataIndex, mobile, render, style, styleRow }) => {
                    return (
                      <div
                        className={cn(
                          'contents max-[600px]:flex max-[600px]:w-full max-[600px]:justify-between max-[600px]:border-b max-[600px]:border-b-[#cacaca] max-[600px]:last:border-b-0 max-[600px]:last:[&_.cell]:w-full max-[600px]:last:[&_.cell]:justify-center',
                          mobile?.direction === 'column' && 'max-[600px]:flex-col max-[600px]:py-4'
                        )}
                        key={key}
                      >
                        <div
                          className={
                            'hidden max-[600px]:flex max-[600px]:items-center max-[600px]:text-xs max-[600px]:text-[#086c7c]'
                          }
                        >
                          {title}
                        </div>

                        <div
                          className={
                            'cell flex items-center border-b border-border px-3 py-[11px] text-[13px] text-foreground max-[600px]:border-b-0'
                          }
                          key={key}
                          style={{
                            ...castFunction(style, item),
                            ...castFunction(styleRow, item)
                          }}
                        >
                          {!!render ? render(item) : get(item, dataIndex ?? key)}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {!!ExpandedRow && hasClickable && (
                  <Expanded
                    expanded={activeRow === index}
                    style={{
                      gridColumn: `1 / ${columns.length + 1}`
                    }}
                  >
                    <div className={'expandedRowWrapper'}>
                      <ExpandedRow expanded={activeRow === index} extras={expandedExtras} item={item} />
                    </div>
                  </Expanded>
                )}
              </Fragment>
            );
          })}

          {!!pagination && (
            <TablePagination changePageSize={changePageSize} columnsLength={columns.length} pagination={pagination} />
          )}

          {footer}
        </div>

        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center bg-white/30 p-10 text-[#1d9333] transition-all duration-[350ms]',
            isLoading ? 'visible opacity-100' : 'pointer-events-none invisible opacity-0'
          )}
        >
          <Spinner className={'h-10 w-10'} />
        </div>
      </div>
    </div>
  );
}
